import { useState } from 'react';
import { Loader2, MapPin, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function Collapsible({ title, badge, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded p-2">
      <button
        className="flex items-center gap-2 w-full text-left text-sm font-medium"
        onClick={() => setOpen(!open)}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {title}
        {badge && <Badge variant="outline" className="ml-auto text-xs">{badge}</Badge>}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

const BUCKET_LABELS = {
  has_anchor: { label: 'Has anchor date', variant: 'default' },
  booking_not_joined: { label: 'Booking not joined', variant: 'destructive' },
  date_null_at_source: { label: 'Date null at source', variant: 'destructive' },
  managed_property: { label: 'Managed property', variant: 'secondary' },
  channel_mismatch: { label: 'Channel mismatch', variant: 'secondary' },
  other: { label: 'Other', variant: 'outline' },
};

const BUCKET_ORDER = [
  'has_anchor',
  'booking_not_joined',
  'date_null_at_source',
  'managed_property',
  'channel_mismatch',
  'other',
];

function BucketTable({ invoices }) {
  if (!invoices || invoices.length === 0) return <p className="text-xs text-muted-foreground">No invoices</p>;
  return (
    <div className="overflow-x-auto">
      <table className="text-[11px] w-full">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="pr-2">Invoice #</th>
            <th className="pr-2">Channel</th>
            <th className="pr-2">Amount</th>
            <th className="pr-2">Uplisting ID</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv, i) => (
            <tr key={inv.invoice_id || i} className="border-t border-muted">
              <td className="pr-2 font-mono">{inv.invoice_number}</td>
              <td className="pr-2">{inv.channel}</td>
              <td className="pr-2">{inv.amount} {inv.currency}</td>
              <td className="pr-2 font-mono">{inv.uplisting_id || '-'}</td>
              <td className="text-muted-foreground break-all">{inv.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TraceCard({ trace }) {
  if (!trace || !trace.found) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
        <strong>{trace?.invoice_number || 'F-2026-06-42'}</strong>: not found in the open invoice set.
        It may already be paid, a credit note, or outside the Pennylane fetch window.
      </div>
    );
  }
  const d = trace.detail;
  return (
    <div className="bg-muted rounded p-3 space-y-1 text-xs">
      <div className="text-sm font-medium">Trace: {d.invoice_number}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <div>Bucket: <Badge variant={BUCKET_LABELS[d.bucket]?.variant || 'outline'}>{d.bucket}</Badge></div>
        <div>Amount: {d.amount} {d.currency}</div>
        <div>Channel: {d.channel}</div>
        <div>External ref: <span className="font-mono">{d.external_reference}</span></div>
        <div>Uplisting ID: <span className="font-mono">{d.uplisting_id || 'none'}</span></div>
        <div>Booking found: {d.booking_found ? 'yes' : 'no'}</div>
        {d.booking_check_in && <div>Booking check_in: {d.booking_check_in}</div>}
        {d.booking_check_out && <div>Booking check_out: {d.booking_check_out}</div>}
        {d.booking_channel && <div>Booking channel: {d.booking_channel}</div>}
        {d.booking_property_id && <div>Property ID: {d.booking_property_id}</div>}
        {d.property_is_managed !== undefined && (
          <div>Managed property: {d.property_is_managed ? 'yes' : 'no'}</div>
        )}
      </div>
      <div className="mt-1">Reason: {d.reason}</div>
    </div>
  );
}

export function AnchorDateCoveragePanel() {
  const [entityId, setEntityId] = useState('afpv');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.anchorDateCoverage({
        entity_id: entityId,
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const buckets = result?.buckets || {};

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <MapPin className="h-5 w-5 text-violet-600" />
          Anchor-Date Coverage
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Classifies every open invoice by whether it has a channel-correct anchor date
          (check_out for Booking.com, check_in for Airbnb). Invoices without an anchor
          date cannot match via the date window, regardless of amount.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Entity</label>
            <select
              className="block mt-1 border rounded px-2 py-1.5 text-sm bg-background"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
            >
              <option value="afpv">AFPV Invest (afpv)</option>
              <option value="bi_sas">Bonjour Investments SAS (bi_sas)</option>
              <option value="bi_immo">Bonjour Immobilier (bi_immo)</option>
              <option value="sci_113">SCI 113 Rue Saint-Honore (sci_113)</option>
              <option value="pfv">PFV Holding (pfv)</option>
            </select>
          </div>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Run Diagnostic
          </Button>
        </div>

        {result?.error && (
          <div className="text-sm text-destructive bg-destructive/10 rounded p-2">{result.error}</div>
        )}

        {result && result.status === 'ok' && (
          <div className="space-y-4">
            {/* Summary bar */}
            <div className="text-sm">
              <strong>{result.total_open_invoices}</strong> open invoices for{' '}
              <strong>{result.entity_name}</strong>
            </div>

            {/* Bucket breakdown */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {BUCKET_ORDER.map((key) => {
                const b = buckets[key];
                if (!b) return null;
                const meta = BUCKET_LABELS[key] || { label: key, variant: 'outline' };
                return (
                  <div key={key} className="border rounded p-2 text-center">
                    <Badge variant={meta.variant} className="text-xs">{meta.label}</Badge>
                    <div className="text-2xl font-bold mt-1">{b.count}</div>
                    <div className="text-xs text-muted-foreground">{b.sum_eur} EUR</div>
                  </div>
                );
              })}
            </div>

            {/* Per-channel and all-channel anchor subtotals */}
            {result.anchor_by_channel && (
              <div className="border rounded p-3 space-y-2">
                <div className="text-sm font-medium">Anchor coverage (compare against recon card)</div>
                <div className="text-xs text-muted-foreground">
                  Per-channel (channel-correct anchor only):
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Booking.com with check_out: <strong>{result.anchor_by_channel.booking_com_checkout}</strong></div>
                  <div>Airbnb with check_in: <strong>{result.anchor_by_channel.airbnb_checkin}</strong></div>
                </div>
                {result.anchor_all_channels && (
                  <>
                    <div className="text-xs text-muted-foreground mt-1">
                      All channels (matches recon card map sizes):
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Any channel with check_out: <strong>{result.anchor_all_channels.any_channel_with_checkout}</strong></div>
                      <div>Any channel with check_in: <strong>{result.anchor_all_channels.any_channel_with_checkin}</strong></div>
                    </div>
                  </>
                )}
                {result.reconciliation_note && (
                  <p className="text-xs text-muted-foreground mt-1">{result.reconciliation_note}</p>
                )}
              </div>
            )}

            {/* F-2026-06-42 trace */}
            <Collapsible
              title="Trace: F-2026-06-42 (535.36 EUR)"
              badge={result.target_trace?.found ? result.target_trace.detail.bucket : 'not found'}
              defaultOpen
            >
              <TraceCard trace={result.target_trace} />
            </Collapsible>

            {/* Per-bucket expandable lists */}
            {BUCKET_ORDER.map((key) => {
              const b = buckets[key];
              if (!b || b.count === 0) return null;
              const meta = BUCKET_LABELS[key] || { label: key, variant: 'outline' };
              return (
                <Collapsible
                  key={key}
                  title={`${meta.label} invoices`}
                  badge={`${b.count} / ${b.sum_eur} EUR`}
                  defaultOpen={key !== 'has_anchor'}
                >
                  <BucketTable invoices={b.invoices} />
                </Collapsible>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
